import type {
  FmEvidenceClaim,
  FmEvidenceDescriptor,
  FmEvidenceFieldProvenance,
} from './FmEvidence.types';

const UNSUPPORTED_CERTAINTY = /\b(confidence|confident|accuracy|accurate|truth|truthful|certainty|certain|trusted|trustworthy)\b/i;
const UNSUPPORTED_RANKING = /\b(best|preferred|superior|definitive|authoritative)\s+source\b|\bsource\s+(winner|ranking|ranked|outranks?)\b/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const RFC3339 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

function fail(path: string, message: string): never {
  throw new Error(`Invalid evidence descriptor: ${path} ${message}.`);
}

function object(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) fail(path, 'must be an object');
  return value as Record<string, unknown>;
}

function exact(value: Record<string, unknown>, allowed: readonly string[], path: string): void {
  const extras = Object.keys(value).filter((key) => !allowed.includes(key));
  if (extras.length) fail(path, `contains unsupported fields: ${extras.join(', ')}`);
}

function text(value: unknown, path: string, optional = false): string | undefined {
  if (value === undefined && optional) return undefined;
  if (typeof value !== 'string' || !value.trim()) fail(path, 'must be non-empty plain text');
  if (UNSUPPORTED_CERTAINTY.test(value)) fail(path, 'uses unsupported certainty vocabulary');
  if (UNSUPPORTED_RANKING.test(value)) fail(path, 'uses unsupported universal source ranking');
  return value;
}

function timestamp(value: unknown, path: string): void {
  if (value === undefined) return;
  if (typeof value !== 'string' || (!ISO_DATE.test(value) && !RFC3339.test(value))) {
    fail(path, 'must be an ISO date or RFC3339 timestamp');
  }
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) fail(path, 'must be a valid calendar date');
  const datePart = value.slice(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);
  const normalizedDate = new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);
  if (normalizedDate !== datePart) {
    fail(path, 'must be a valid calendar date');
  }
}

function scalar(value: unknown, path: string): void {
  if (value !== null && !['string', 'number', 'boolean'].includes(typeof value)) {
    fail(path, 'must be a scalar value');
  }
  if (typeof value === 'number' && !Number.isFinite(value)) fail(path, 'must be finite');
  if (typeof value === 'string') text(value, path);
}

function list(value: unknown, path: string, minimum = 1): unknown[] {
  if (!Array.isArray(value) || value.length < minimum) fail(path, `must contain at least ${minimum} item${minimum === 1 ? '' : 's'}`);
  return value;
}

function uniqueIds(items: unknown[], path: string): Set<string> {
  const ids = new Set<string>();
  items.forEach((item, index) => {
    const id = text(object(item, `${path}[${index}]`).id, `${path}[${index}].id`)!;
    if (ids.has(id)) fail(`${path}[${index}].id`, 'must be unique');
    ids.add(id);
  });
  return ids;
}

function state(
  value: unknown,
  path: string,
  statuses: readonly string[],
  supportsFields = false,
): void {
  const item = object(value, path);
  exact(item, supportsFields
    ? ['status', 'reasonCode', 'label', 'detail', 'fieldIds']
    : ['status', 'reasonCode', 'label', 'detail'], path);
  if (!statuses.includes(String(item.status))) fail(`${path}.status`, 'contains an unsupported value');
  text(item.reasonCode, `${path}.reasonCode`);
  /* `label` is an OPTIONAL override for a source that supplies its own
     owner-facing copy. When it is absent FmEvidence localizes the status, which
     is what keeps the pane from mixing languages. An
     empty string is still refused: that would render a blank chip. */
  text(item.label, `${path}.label`, true);
  text(item.detail, `${path}.detail`, true);
  if (supportsFields && item.fieldIds !== undefined) {
    const fields = list(item.fieldIds, `${path}.fieldIds`);
    fields.forEach((field, index) => text(field, `${path}.fieldIds[${index}]`));
  }
}

function provenance(
  value: unknown,
  path: string,
  sourceIds: Set<string>,
  citationSources: Map<string, string>,
  dates: Record<string, unknown> | undefined,
): asserts value is FmEvidenceFieldProvenance {
  const item = object(value, path);
  exact(item, ['sourceIds', 'citationIds', 'dateKind'], path);
  const dateKind = String(item.dateKind);
  if (!['observed', 'retrieved', 'effective', 'unavailable'].includes(dateKind)) {
    fail(`${path}.dateKind`, 'contains an unsupported value');
  }
  const minimum = dateKind === 'unavailable' ? 0 : 1;
  const sources = list(item.sourceIds, `${path}.sourceIds`, minimum);
  sources.forEach((id, index) => {
    const sourceId = text(id, `${path}.sourceIds[${index}]`)!;
    if (!sourceIds.has(sourceId)) fail(`${path}.sourceIds[${index}]`, 'must reference a declared source');
  });
  const citations = list(item.citationIds, `${path}.citationIds`, minimum);
  citations.forEach((id, index) => {
    const citationId = text(id, `${path}.citationIds[${index}]`)!;
    const citationSource = citationSources.get(citationId);
    if (!citationSource) fail(`${path}.citationIds[${index}]`, 'must reference a declared citation');
    if (!sources.includes(citationSource)) {
      fail(`${path}.citationIds[${index}]`, 'must cite one of the field provenance sources');
    }
  });
  if (dateKind !== 'unavailable') {
    const key = `${dateKind}At`;
    if (!dates?.[key]) fail(`${path}.dateKind`, `requires dates.${key}`);
  }
}

function claim(
  value: unknown,
  path: string,
  sourceIds: Set<string>,
  citationSources: Map<string, string>,
  dates: Record<string, unknown> | undefined,
): asserts value is FmEvidenceClaim {
  const item = object(value, path);
  exact(item, ['id', 'label', 'value', 'unit', 'explanation', 'provenance'], path);
  text(item.id, `${path}.id`);
  text(item.label, `${path}.label`);
  scalar(item.value, `${path}.value`);
  text(item.unit, `${path}.unit`, true);
  text(item.explanation, `${path}.explanation`, true);
  if (!item.provenance) fail(`${path}.provenance`, 'is required for every claim');
  provenance(item.provenance, `${path}.provenance`, sourceIds, citationSources, dates);
  const itemProvenance = object(item.provenance, `${path}.provenance`);
  const claimSourceIds = list(itemProvenance.sourceIds, `${path}.provenance.sourceIds`, 0);
  const claimCitationIds = list(itemProvenance.citationIds, `${path}.provenance.citationIds`, 0);
  if (!sourceIds.size && item.value !== null) fail(`${path}.value`, 'must be null when no source is served');
  if (item.value !== null && itemProvenance.dateKind === 'unavailable') {
    fail(`${path}.value`, 'must be null when the claim date is unavailable');
  }
  if (!sourceIds.size && (claimSourceIds.length
    || claimCitationIds.length
    || itemProvenance.dateKind !== 'unavailable')) {
    fail(`${path}.provenance`, 'must be empty and unavailable when no source is served');
  }
}

function disclosure(value: unknown, path: string): void {
  const item = object(value, path);
  exact(item, ['showLabel', 'hideLabel', 'caption'], path);
  text(item.showLabel, `${path}.showLabel`);
  text(item.hideLabel, `${path}.hideLabel`);
  text(item.caption, `${path}.caption`);
}

// eslint-disable-next-line complexity -- One fail-closed validator mirrors the three-member sealed descriptor union.
export function assertFmEvidenceDescriptor(value: unknown): asserts value is FmEvidenceDescriptor {
  const evidence = object(value, 'evidence');
  const common = [
    'kind', 'id', 'title', 'summary', 'ariaLabel', 'basis', 'availability',
    'restriction', 'freshness', 'missingness', 'dates', 'sources', 'citations',
    'ownerAction',
  ];
  const kind = String(evidence.kind);
  const payload = kind === 'evidence-band'
    ? ['claim', 'expertDisclosure']
    : ['rows', 'expertDisclosure'];
  exact(evidence, [...common, ...payload], 'evidence');
  if (!['evidence-band', 'evidence-rows', 'source-comparison'].includes(kind)) {
    fail('evidence.kind', 'contains an unsupported value');
  }
  text(evidence.id, 'evidence.id');
  text(evidence.title, 'evidence.title');
  text(evidence.summary, 'evidence.summary');
  text(evidence.ariaLabel, 'evidence.ariaLabel');
  if (!['measured', 'modelled', 'recorded', 'mixed'].includes(String(evidence.basis))) {
    fail('evidence.basis', 'must be measured, modelled, recorded, or mixed');
  }
  state(evidence.availability, 'evidence.availability', ['available', 'partial', 'unavailable', 'error']);
  state(evidence.restriction, 'evidence.restriction', ['open', 'restricted']);
  state(evidence.freshness, 'evidence.freshness', ['current', 'stale', 'unavailable']);
  state(evidence.missingness, 'evidence.missingness', ['complete', 'partial', 'missing'], true);

  let dates: Record<string, unknown> | undefined;
  if (evidence.dates !== undefined) {
    dates = object(evidence.dates, 'evidence.dates');
    exact(dates, ['observedAt', 'retrievedAt', 'effectiveAt'], 'evidence.dates');
    ['observedAt', 'retrievedAt', 'effectiveAt'].forEach((key) => timestamp(dates?.[key], `evidence.dates.${key}`));
    if (!Object.values(dates).some(Boolean)) fail('evidence.dates', 'must include at least one explicit date');
  }
  const availability = object(evidence.availability, 'evidence.availability');
  if (['available', 'partial'].includes(String(availability.status)) && !dates) {
    fail('evidence.dates', 'is required when evidence is available or partial');
  }

  const availabilityStatus = String(availability.status);
  const minimumSources = kind === 'source-comparison'
    ? 2
    : ['available', 'partial'].includes(availabilityStatus) ? 1 : 0;
  const sources = list(evidence.sources, 'evidence.sources', minimumSources);
  const sourceIds = uniqueIds(sources, 'evidence.sources');
  const sourceBases = new Set<string>();
  sources.forEach((source, index) => {
    const item = object(source, `evidence.sources[${index}]`);
    exact(item, ['id', 'label', 'dataset', 'basis', 'revision'], `evidence.sources[${index}]`);
    text(item.label, `evidence.sources[${index}].label`);
    text(item.dataset, `evidence.sources[${index}].dataset`);
    text(item.revision, `evidence.sources[${index}].revision`, true);
    if (!['measured', 'modelled', 'recorded', 'mixed'].includes(String(item.basis))) {
      fail(`evidence.sources[${index}].basis`, 'contains an unsupported value');
    }
    sourceBases.add(String(item.basis));
  });
  if (sourceBases.size > 1 && evidence.basis !== 'mixed') {
    fail('evidence.basis', 'must be mixed when declared sources use different bases');
  }
  if (evidence.basis !== 'mixed' && [...sourceBases].some((basis) => basis !== evidence.basis)) {
    fail('evidence.basis', 'must match the declared source basis');
  }
  if (evidence.basis === 'mixed'
    && !sourceBases.has('mixed')
    && sourceBases.size < 2) {
    fail('evidence.basis', 'mixed requires distinct source bases or an explicitly mixed source');
  }

  const citations = list(evidence.citations, 'evidence.citations', sources.length ? 1 : 0);
  if (!sources.length && citations.length) fail('evidence.citations', 'must be empty when no source is served');
  uniqueIds(citations, 'evidence.citations');
  const citationSources = new Map<string, string>();
  citations.forEach((citation, index) => {
    const item = object(citation, `evidence.citations[${index}]`);
    exact(item, ['id', 'sourceId', 'label', 'reference'], `evidence.citations[${index}]`);
    const sourceId = text(item.sourceId, `evidence.citations[${index}].sourceId`)!;
    if (!sourceIds.has(sourceId)) fail(`evidence.citations[${index}].sourceId`, 'must reference a declared source');
    citationSources.set(String(item.id), sourceId);
    text(item.label, `evidence.citations[${index}].label`);
    text(item.reference, `evidence.citations[${index}].reference`, true);
  });

  let presentedFieldIds: Set<string>;
  if (kind === 'evidence-band') {
    claim(evidence.claim, 'evidence.claim', sourceIds, citationSources, dates);
    presentedFieldIds = new Set([String(object(evidence.claim, 'evidence.claim').id)]);
    if (evidence.expertDisclosure !== undefined) fail('evidence.expertDisclosure', 'is not supported by evidence-band');
  } else if (kind === 'evidence-rows') {
    const rows = list(evidence.rows, 'evidence.rows');
    presentedFieldIds = uniqueIds(rows, 'evidence.rows');
    rows.forEach((row, index) => claim(row, `evidence.rows[${index}]`, sourceIds, citationSources, dates));
    if (evidence.expertDisclosure !== undefined) disclosure(evidence.expertDisclosure, 'evidence.expertDisclosure');
  } else {
    const rows = list(evidence.rows, 'evidence.rows');
    presentedFieldIds = uniqueIds(rows, 'evidence.rows');
    rows.forEach((row, rowIndex) => {
      const item = object(row, `evidence.rows[${rowIndex}]`);
      exact(item, ['id', 'label', 'values'], `evidence.rows[${rowIndex}]`);
      text(item.label, `evidence.rows[${rowIndex}].label`);
      const values = list(item.values, `evidence.rows[${rowIndex}].values`, 2);
      const seenSources = new Set<string>();
      values.forEach((entry, valueIndex) => {
        const path = `evidence.rows[${rowIndex}].values[${valueIndex}]`;
        const comparison = object(entry, path);
        exact(comparison, ['sourceId', 'value', 'unit', 'availability', 'provenance'], path);
        const sourceId = text(comparison.sourceId, `${path}.sourceId`)!;
        if (!sourceIds.has(sourceId)) fail(`${path}.sourceId`, 'must reference a declared source');
        if (seenSources.has(sourceId)) fail(`${path}.sourceId`, 'must be unique within the comparison row');
        seenSources.add(sourceId);
        scalar(comparison.value, `${path}.value`);
        text(comparison.unit, `${path}.unit`, true);
        state(comparison.availability, `${path}.availability`, ['available', 'partial', 'unavailable', 'error']);
        provenance(comparison.provenance, `${path}.provenance`, sourceIds, citationSources, dates);
        const comparisonProvenance = object(comparison.provenance, `${path}.provenance`);
        const provenanceSources = list(comparisonProvenance.sourceIds, `${path}.provenance.sourceIds`, 0);
        if (comparison.value !== null && comparisonProvenance.dateKind === 'unavailable') {
          fail(`${path}.value`, 'must be null when the comparison date is unavailable');
        }
        if (!provenanceSources.includes(sourceId)) {
          fail(`${path}.provenance.sourceIds`, 'must include the comparison value source');
        }
      });
      if (seenSources.size !== sourceIds.size) fail(`${item.values ? `evidence.rows[${rowIndex}].values` : 'evidence.rows'}`, 'must compare every declared source');
    });
    if (evidence.expertDisclosure !== undefined) disclosure(evidence.expertDisclosure, 'evidence.expertDisclosure');
  }

  const missingness = object(evidence.missingness, 'evidence.missingness');
  if (Array.isArray(missingness.fieldIds)) {
    missingness.fieldIds.forEach((fieldId, index) => {
      if (!presentedFieldIds.has(String(fieldId))) {
        fail(`evidence.missingness.fieldIds[${index}]`, 'must reference a presented field');
      }
    });
  }

  if (evidence.ownerAction !== undefined) {
    const action = object(evidence.ownerAction, 'evidence.ownerAction');
    exact(action, ['label', 'ownerPageId', 'onOpen'], 'evidence.ownerAction');
    text(action.label, 'evidence.ownerAction.label');
    text(action.ownerPageId, 'evidence.ownerAction.ownerPageId');
    if (typeof action.onOpen !== 'function') fail('evidence.ownerAction.onOpen', 'must be a callback');
  }
}
